import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useScrollTrigger,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { colors } from '../theme';

const menuItems = [
  { label: 'Home', href: '#home' },
  { label: 'Solutions', href: '#solutions' },
  { label: 'Technology', href: '#technology' },
  { label: 'Workflow', href: '#workflow' },
  { label: 'Financial Plans', href: '#financial' },
  { label: 'Projects', href: '#projects' },
  { label: 'Products', href: '#products' },
  { label: 'Partners', href: '#partners' },
  { label: 'Contact', href: '#contact' },
];

function NavigationBar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50,
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavClick = (href) => {
    setMobileOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const drawer = (
    <Box
      sx={{
        width: 280,
        height: '100%',
        bgcolor: colors.black,
        color: colors.white,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
        }}
      >
        <Box
          component="img"
          src="/logo-white.png"
          alt="SolaCharge"
          sx={{ height: 40 }}
        />
        <IconButton onClick={handleDrawerToggle} sx={{ color: colors.white }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <ListItemButton onClick={() => handleNavClick(item.href)}>
              <ListItemText
                primary={item.label}
                sx={{
                  '& .MuiListItemText-primary': {
                    color: colors.white,
                    fontWeight: 500,
                    '&:hover': {
                      color: colors.yellow,
                    },
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem sx={{ mt: 2 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => handleNavClick('#contact')}
            sx={{
              bgcolor: colors.yellow,
              color: colors.black,
              '&:hover': {
                bgcolor: colors.lightYellow,
              },
            }}
          >
            Start Your Project
          </Button>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={trigger ? 4 : 0}
        sx={{
          bgcolor: trigger ? colors.black : 'rgba(0,0,0,0.95)',
          transition: 'all 0.3s ease',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
          {/* Logo */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              gap: 1.5,
            }}
            onClick={() => handleNavClick('#home')}
          >
            <Box
              component="img"
              src="/logo-white.png"
              alt="SolaCharge"
              sx={{ 
                height: 35,
                objectFit: 'contain',
              }}
              onError={(e) => {
                console.error('Logo failed to load');
                e.target.src = '/logo.png';
              }}
            />
            <Box
              sx={{
                color: colors.yellow,
                fontWeight: 700,
                fontSize: '1.3rem',
                letterSpacing: '-0.5px',
              }}
            >
              SolaCharge
            </Box>
          </Box>

          {/* Desktop Menu */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {menuItems.map((item) => (
                <Button
                  key={item.label}
                  onClick={() => handleNavClick(item.href)}
                  sx={{
                    color: colors.white,
                    px: 2,
                    '&:hover': {
                      color: colors.yellow,
                      bgcolor: 'transparent',
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
              <Button
                variant="contained"
                onClick={() => handleNavClick('#contact')}
                sx={{
                  ml: 2,
                  bgcolor: colors.yellow,
                  color: colors.black,
                  '&:hover': {
                    bgcolor: colors.lightYellow,
                  },
                }}
              >
                Start Your Project
              </Button>
            </Box>
          )}

          {/* Mobile Menu Icon */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ color: colors.yellow }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {drawer}
      </Drawer>

      {/* Spacer for fixed AppBar */}
      <Toolbar />
    </>
  );
}

export default NavigationBar;
